import getConfig from '../config';

import { countryToRegion } from './country-mappings';
import { formatFilters, formatSearchQueries } from './formatting';
import { getApplicationSourceName, isRetailLogin } from './iframeMessaging';

const config = getConfig();

/* Authorization */
const OktaException = (message) => ({ message, name: 'OktaException' });

export const postCodeToOkta = async () => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search.slice(1));
  const code = searchParams.get('code');
  if (!code) {
    const error = searchParams.get('error') || 'Unauthorized';
    // eslint-disable-next-line fp/no-throw
    throw new OktaException(error);
  }
  const codeVerifier = localStorage.getItem('code_verifier');
  const params = new URLSearchParams();

  params.set('code', code);
  params.set('code_verifier', codeVerifier);
  params.set('grant_type', 'authorization_code');
  params.set('redirect_uri', `${window.location.origin}/auth`);
  params.set('client_id', config.oktaClientID);

  const options = {
    body: params,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    method: 'POST',
  };
  const uri = config.oktaAegisHost + config.oktaTokenResource;

  return fetch(uri, options).then(async (res) => {
    if (res.status !== 200) {
      // eslint-disable-next-line camelcase,fp/no-throw
      throw Error(res.reasons?.error_description);
    }
    return res.json();
  });
};

/* Storeviews */
export const getStore = async (country, storeNumber) => {
  const options = {
    headers: {
      Accept: 'application/json',
      'nike-api-caller-id': 'com.nike:test-caller-id',
    },
    method: 'GET',
  };

  const uri = `${config.storeviewsHost}${config.storeviewsResource}?search=(address.country==${country} and storeNumber==${storeNumber})&fields=address,currencies,id,locale,name,phone,timezone`;
  return fetch(uri, options)
  .then(async (res) => {
    if (!res.ok) {
      // eslint-disable-next-line camelcase,fp/no-throw
      throw await res.json();
    }
    return res.json();
  })
  .then((body) => {
    const store = body.objects[0];
      return ({
        currency: store.currencies[0], // TODO: allow for all currencies
        id: store.id,
        locale: store.locale,
        storeHeaderInfo: {
          address: store.address,
          name: store.name,
          phoneNumber: store.phone,
        },
        timezone: store.timezone,
      });
  });
};

export const getCountryByStoreId = async (storeId) => {
  const options = {
    headers: {
      Accept: 'application/json',
      'nike-api-caller-id': 'com.nike:test-caller-id',
    },
    method: 'GET',
  };
  const uri = `${config.storeviewsHost}${config.storeviewsResource}/${storeId}?fields=address,currencies,id,locale,name,phone,timezone`;
  return fetch(uri, options)
  .then(async (res) => {
    if (!res.ok) {
      // eslint-disable-next-line camelcase,fp/no-throw
      throw await res.json();
    }
    return res.json();
  })
  .then((body) => ({ body }));
};

export const storeViewsByCountry = async (country, published, fields) => {
  const options = {
    headers: {
      Accept: 'application/json',
      'nike-api-caller-id': 'com.nike:test-caller-id',
    },
    method: 'GET',
  };
  const uri = `${config.storeviewsHost}${config.storeviewsResource}?search=(address.country==${country} and company==Nike and facilityType==NIKE_OWNED_STORE and published==${published})&count=3000${fields ? `&fields=${fields.join(',')}` : ''}`;
  return fetch(uri, options)
  .then(async (res) => {
    if (!res.ok) {
      // eslint-disable-next-line camelcase,fp/no-throw
      throw await res.json();
    }
    return res.json();
  })
  .then((body) => ({ body }));
};

/* Electronic Journal */

export const getTransactions = (country, ejSearchFields, jwt) => {
  const ejRegion = countryToRegion(country);
  const filters = formatFilters(ejSearchFields.filters);
  const searchQueries = formatSearchQueries(ejSearchFields);
  const query = [...filters, ...searchQueries].join('&');
  const headers = {
    Authorization: `Bearer ${jwt}`,
    authsource: getApplicationSourceName(),
    'Content-Type': 'application/json'
  };
  if (isRetailLogin()) {
    // eslint-disable-next-line fp/no-mutation
    headers.authtype = 'retail';
  }
  const uri = ejRegion === 'cn-northwest-1'
  ? `${config.electronicJournalCnHost.replace('{{ej_region}}', ejRegion)}${config.electronicJournalResource}?${query}`
  : `${config.electronicJournalHost.replace('{{ej_region}}', ejRegion)}${config.electronicJournalResource}?${query}`;

  const options = {
    headers,
    method: 'GET',
  };

  return fetch(uri, options)
  .then(async (res) => {
    if (!res.ok) {
      // eslint-disable-next-line camelcase,fp/no-throw
      throw await res.json();
    }
    return res.json();
  })
  .then((body) => ({ body }));
};

export const getStorePrinters = async (country, storeId, jwt) => {
  const ejRegion = countryToRegion(country);
  const headers = {
    Authorization: `Bearer ${jwt}`,
    authsource: getApplicationSourceName(),
    'Content-Type': 'application/json'
  };
  if (isRetailLogin()) {
    // eslint-disable-next-line fp/no-mutation
    headers.authtype = 'retail';
  }
  const uri = ejRegion === 'cn-northwest-1'
  ? `${config.electronicJournalCnHost.replace('{{ej_region}}', ejRegion)}${config.storePrintersResource}?filter=storeId(${storeId})`
  : `${config.electronicJournalHost.replace('{{ej_region}}', ejRegion)}${config.storePrintersResource}?filter=storeId(${storeId})`;

  const options = {
    headers,
    method: 'GET',
  };

  return fetch(uri, options)
  .then(async (res) => {
    if (!res.ok) {
      // eslint-disable-next-line camelcase,fp/no-throw
      throw await res.json();
    }
    return res.json();
  })
  .then((body) => ({ body }))
  .catch(() => ({ body: [] }));
};

export const fetchReceiptForTransaction = async (country, storeId, transactionId, jwt) => {
  const ejRegion = countryToRegion(country);
  const headers = {
    Authorization: `Bearer ${jwt}`,
    authsource: getApplicationSourceName(),
    'Content-Type': 'application/json'
  };
  if (isRetailLogin()) {
    // eslint-disable-next-line fp/no-mutation
    headers.authtype = 'retail';
  }
  const uri = ejRegion === 'cn-northwest-1'
  ? `${config.electronicJournalCnHost.replace('{{ej_region}}', ejRegion)}${config.fetchReceiptResource}?filter=storeId(${storeId})&filter=transactionId(${transactionId})`
  : `${config.electronicJournalHost.replace('{{ej_region}}', ejRegion)}${config.fetchReceiptResource}?filter=storeId(${storeId})&filter=transactionId(${transactionId})`;

  const options = {
    headers,
    method: 'GET',
  };

  return fetch(uri, options)
  .then(async (res) => {
    if (!res.ok) {
      // eslint-disable-next-line camelcase,fp/no-throw
      throw await res.json();
    }
    return res.json();
  })
  .then((body) => ({ body }))
  .catch(() => ({ body: [] }));
};

export const reportReprintEvent = async (country, payload, jwt) => {
  const ejRegion = countryToRegion(country);
  const headers = {
    Authorization: `Bearer ${jwt}`,
    authsource: getApplicationSourceName(),
    'Content-Type': 'application/json',
  };
  if (isRetailLogin()) {
    // eslint-disable-next-line fp/no-mutation
    headers.authtype = 'retail';
  }

  const uri = `${config.electronicJournalHost.replace('{{ej_region}}', ejRegion)}${config.reprintEventResource}`;

  const options = {
    body: JSON.stringify(payload),
    headers,
    method: 'POST',
  };

  return fetch(uri, options)
  .then(async (res) => {
    if (!res.ok) {
      // eslint-disable-next-line camelcase,fp/no-throw
      throw await res.json();
    }
    return res.json();
  })
  .then((body) => ({ body }));
};

export const eventCaptured = (country, data, jwt) => {
  const ejRegion = countryToRegion(country);
  const headers = {
    Authorization: `Bearer ${jwt}`,
    'Content-Type': 'application/json',
  };
  const query = formatFilters(data).join('&');
  if (isRetailLogin()) {
    // eslint-disable-next-line fp/no-mutation
    headers.authtype = 'retail';
  }

  const uri = ejRegion === 'cn-northwest-1'
    ? `${config.electronicJournalCnHost.replace('{{ej_region}}', ejRegion)}${config.eventCapturedResource}?${query}`
    : `${config.electronicJournalHost.replace('{{ej_region}}', ejRegion)}${config.eventCapturedResource}?${query}`;

  const options = {
    headers,
    method: 'GET',
  };

  return fetch(uri, options)
  .then(async (res) => {
    if (!res.ok) {
      // eslint-disable-next-line camelcase,fp/no-throw
      throw await res.json();
    }
    return res.json();
  })
  .then((body) => ({ body }));
};
